// UserAuth.js - Firebase Auth wrapper for compatibility with existing code
import useAuthStore from '../../stores/useAuthStore';
import { signOutUser, refreshAccountToken } from '../../services/auth.service';

/**
 * Check if the user is currently signed in
 * Uses Firebase auth state from Zustand store
 */
export const isUserSignedIn = () => {
    return useAuthStore.getState().isAuthenticated();
};

/**
 * Get the stored Google access token (primary account)
 * Firebase provides this after Google sign-in
 */
export const getGoogleAccessToken = () => {
    return useAuthStore.getState().getGoogleAccessToken();
};

/**
 * Get token for a specific Google account
 * @param {string} email - The email of the account
 * @returns {string|null} The access token or null
 */
export const getAccountToken = (email) => {
    return useAuthStore.getState().getAccountToken(email);
};

/**
 * Get all connected Google accounts
 * @returns {Array} List of connected accounts with email and user info
 */
export const getConnectedAccounts = () => {
    return useAuthStore.getState().getConnectedAccounts();
};

/**
 * Check if a specific account is connected
 * @param {string} email - The email to check
 * @returns {boolean}
 */
export const isAccountConnected = (email) => {
    return useAuthStore.getState().isAccountConnected(email);
};

/**
 * Store Google access token for an account
 * @param {string} email - The account email
 * @param {string} accessToken - The access token
 * @param {Object} userInfo - Optional user info
 */
export const setAccountToken = (email, accessToken, userInfo = {}) => {
    useAuthStore.getState().setAccountToken(email, accessToken, userInfo);
};

/**
 * Store Google access token (called after Firebase sign-in)
 * Legacy method - uses the current user's email
 */
export const setGoogleAccessToken = (accessToken, expiryDateMilliseconds) => {
    useAuthStore.getState().setGoogleAccessToken(accessToken);
};

/**
 * Check if token is about to expire (within 5 minutes)
 * For Firebase, we rely on Firebase to manage token refresh
 */
export const isTokenExpiringSoon = () => {
    // Firebase handles token refresh automatically
    // This is kept for compatibility with existing code
    return false;
};

/**
 * Request new tokens for a specific account
 * @param {string} email - The account email to refresh
 */
export const refreshAccountAccessToken = async (email) => {
    return refreshAccountToken(email);
};

/**
 * Request new tokens
 * Firebase handles this automatically, but we expose it for compatibility
 */
export const requestServerNewTokens = async () => {
    // Firebase handles token refresh automatically
};

/**
 * Clear all authentication data on logout
 */
export const cleanUserAuthInfo = () => {
    signOutUser();
};

/**
 * Disconnect a specific Google account
 * @param {string} email - The email of the account to disconnect
 */
export const disconnectAccount = (email) => {
    return useAuthStore.getState().removeAccount(email);
};

/**
 * Validate the current user session
 */
export const validateUser = () => {
    return isUserSignedIn();
};

/**
 * Get the current Firebase user
 */
export const getCurrentUser = () => {
    return useAuthStore.getState().currentUser;
};

/**
 * Check if any Google accounts are connected
 */
export const hasConnectedAccounts = () => {
    return useAuthStore.getState().hasConnectedAccounts();
};
